import type { ForgeAccountDiagnostics } from 'src/Forge/types';

import { getForgeAccountDiagnostics, getForgeViewerToken } from 'src/Forge/api';

export type ForgeViewerConfig = {
  env: string;
  api: string;
};

export type ForgeHttpProbeResult = {
  label: string;
  url: string;
  status: number;
  statusText: string;
  body: unknown;
};

export type ForgeCapturedNetworkRequest = {
  method: string;
  url: string;
  status?: number;
  statusText?: string;
  durationMs?: number;
  error?: string;
};

export type ForgeLoadDiagnosticsReport = {
  documentId: string;
  viewerConfig: ForgeViewerConfig;
  decodedUrn?: string;
  token: {
    expiresIn?: number;
    expiresAt?: string;
    clientId?: string;
    scopes?: string[];
    decodeError?: string;
  };
  manifestProbes: ForgeHttpProbeResult[];
  capturedViewerRequests: ForgeCapturedNetworkRequest[];
  serverAccountDiagnostics: ForgeAccountDiagnostics | null;
  analysis: {
    summary: string;
    likelyCauses: string[];
  };
};

function stripUrnPrefix(documentId: string) {
  return documentId.startsWith('urn:') ? documentId.slice(4) : documentId;
}

function decodeUrnBase64(encodedUrn: string) {
  try {
    return atob(encodedUrn);
  } catch {
    return undefined;
  }
}

function decodeJwtPayload(accessToken: string) {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) {
      return null;
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized)) as {
      exp?: number;
      client_id?: string;
      scope?: string | string[];
    };
  } catch {
    return null;
  }
}

function normalizeJwtScopes(
  scope: string | string[] | undefined,
): string[] | undefined {
  if (scope == null) {
    return undefined;
  }
  if (Array.isArray(scope)) {
    return scope;
  }
  return scope.split(' ').filter(Boolean);
}

function isForgeRelatedUrl(url: string) {
  return /derivative|modelderivative|autodesk\.com/i.test(url);
}

function buildManifestProbeUrls(documentId: string) {
  const urn = stripUrnPrefix(documentId);
  const encodedDocumentId = encodeURIComponent(documentId);
  const encodedUrn = encodeURIComponent(urn);

  return [
    {
      label: 'API manifest (EU / EMEA)',
      url: `https://developer.api.autodesk.com/modelderivative/v2/regions/eu/designdata/${encodedUrn}/manifest`,
    },
    {
      label: 'API manifest (US)',
      url: `https://developer.api.autodesk.com/modelderivative/v2/designdata/${encodedUrn}/manifest`,
    },
    {
      label: 'CDN manifest (global host, EU region path)',
      url: `https://cdn.derivative.autodesk.com/derivativeservice/v2/regions/eu/manifest/${encodedUrn}`,
    },
    {
      label: 'CDN manifest (DEU host, EU region path)',
      url: `https://cdn.derivative.deu.autodesk.com/derivativeservice/v2/regions/eu/manifest/${encodedUrn}`,
    },
    {
      label: 'CDN manifest (DEU host, legacy path)',
      url: `https://cdn.derivative.deu.autodesk.com/derivativeservice/v2/manifest/${encodedUrn}`,
    },
    {
      label: 'Derivative service API manifest (EU)',
      url: `https://developer.api.autodesk.com/derivativeservice/v2/regions/eu/manifest/${encodedUrn}`,
    },
    {
      label: 'Derivative service API manifest (documentId with urn: prefix)',
      url: `https://developer.api.autodesk.com/derivativeservice/v2/regions/eu/manifest/${encodedDocumentId}`,
    },
    {
      label: 'CDN manifest (global host, US path)',
      url: `https://cdn.derivative.autodesk.com/derivativeservice/v2/manifest/${encodedUrn}`,
    },
  ];
}

async function probeUrl(
  label: string,
  url: string,
  accessToken: string,
): Promise<ForgeHttpProbeResult> {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const responseText = await response.text();
  let body: unknown = responseText;
  if (responseText) {
    try {
      body = JSON.parse(responseText);
    } catch {
      // keep raw text
    }
  }

  return {
    label,
    url,
    status: response.status,
    statusText: response.statusText,
    body,
  };
}

function analyzeDiagnostics(
  manifestProbes: ForgeHttpProbeResult[],
  serverAccountDiagnostics: ForgeLoadDiagnosticsReport['serverAccountDiagnostics'],
  capturedViewerRequests: ForgeCapturedNetworkRequest[],
  viewerConfig: ForgeViewerConfig,
): ForgeLoadDiagnosticsReport['analysis'] {
  const likelyCauses: string[] = [];

  const apiEu = manifestProbes.find((p) =>
    p.label.startsWith('API manifest (EU'),
  );
  const apiUs = manifestProbes.find((p) =>
    p.label.startsWith('API manifest (US'),
  );
  const cdnEu = manifestProbes.find((p) =>
    p.label.includes('CDN manifest (global host, EU'),
  );

  if (serverAccountDiagnostics?.authentication.readToken.ok === false) {
    likelyCauses.push(
      'Server cannot obtain a Forge read token — check FORGE_ID/FORGE_SECRET or APS app status.',
    );
  }

  if (serverAccountDiagnostics?.authentication.writeToken.ok === false) {
    likelyCauses.push(
      'Server cannot obtain a Forge write token — translation/upload may be blocked.',
    );
  }

  if (serverAccountDiagnostics?.bucket.accessible === false) {
    likelyCauses.push(
      `Forge OSS bucket "${serverAccountDiagnostics.bucket.name}" is not accessible with current credentials.`,
    );
  }

  if (serverAccountDiagnostics?.regionAlignment?.status === 'wrong_region') {
    likelyCauses.push(serverAccountDiagnostics.regionAlignment.message);
  }

  if (serverAccountDiagnostics?.regionAlignment?.status === 'missing') {
    likelyCauses.push(serverAccountDiagnostics.regionAlignment.message);
  }

  if (apiEu?.status === 404 && apiUs?.status === 200) {
    likelyCauses.push(
      'Manifest exists in US but not EU — viewer uses streamingV2_EU; translations may be in the wrong region.',
    );
  }

  if (apiEu?.status === 404 && apiUs?.status === 404) {
    likelyCauses.push(
      'No manifest in EU or US — translation missing, deleted, or URN points to an object this APS app cannot access.',
    );
  }

  if (apiEu?.status === 200 && cdnEu?.status === 404) {
    likelyCauses.push(
      'API manifest exists but CDN manifest returns 404 — possible APS CDN/routing issue or token scope mismatch for CDN.',
    );
  }

  if (apiEu?.status === 401 || apiEu?.status === 403) {
    likelyCauses.push(
      'Manifest request rejected (401/403) — token may be invalid, expired, or missing viewables:read scope.',
    );
  }

  const viewerCdn404 = capturedViewerRequests.some(
    (r) => r.status === 404 && /cdn\.derivative/i.test(r.url),
  );
  if (viewerCdn404 && apiEu?.status === 200) {
    likelyCauses.push(
      'Viewer CDN request returned 404 while API manifest succeeded — compare captured viewer URL with probe URLs.',
    );
  }

  if (
    viewerConfig.api === 'streamingV2_EU' &&
    apiEu?.status === 404 &&
    apiUs?.status === 404
  ) {
    likelyCauses.push(
      'Viewer is configured for EMEA (streamingV2_EU) but no EMEA/US manifest was found.',
    );
  }

  if (likelyCauses.length === 0) {
    likelyCauses.push(
      'No single root cause identified — inspect individual probe statuses and captured viewer URLs.',
    );
  }

  const summary = [
    apiEu ? `API EU: ${apiEu.status}` : null,
    apiUs ? `API US: ${apiUs.status}` : null,
    cdnEu ? `CDN EU: ${cdnEu.status}` : null,
    capturedViewerRequests.length
      ? `Viewer requests captured: ${capturedViewerRequests.length}`
      : null,
    serverAccountDiagnostics?.authentication.readToken.ok === false
      ? 'Server auth: FAIL'
      : 'Server auth: OK',
  ]
    .filter(Boolean)
    .join(' | ');

  return { summary, likelyCauses };
}

export function installForgeNetworkCapture() {
  const captured: ForgeCapturedNetworkRequest[] = [];
  const originalFetch = window.fetch.bind(window);
  const xhrProto = XMLHttpRequest.prototype;
  const xhrOpenDescriptor = Object.getOwnPropertyDescriptor(xhrProto, 'open');
  const xhrSendDescriptor = Object.getOwnPropertyDescriptor(xhrProto, 'send');
  const nativeXhrOpen = xhrOpenDescriptor?.value as XMLHttpRequest['open'];
  const nativeXhrSend = xhrSendDescriptor?.value as XMLHttpRequest['send'];

  if (
    !nativeXhrOpen ||
    !nativeXhrSend ||
    !xhrOpenDescriptor ||
    !xhrSendDescriptor
  ) {
    throw new Error('Could not install Forge network capture');
  }

  const xhrMetadata = new WeakMap<
    XMLHttpRequest,
    { method: string; url: string; startedAt: number }
  >();

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const request = args[0];
    const url =
      typeof request === 'string'
        ? request
        : request instanceof Request
        ? request.url
        : String(request);

    if (!isForgeRelatedUrl(url)) {
      return originalFetch(...args);
    }

    const startedAt = performance.now();
    const method =
      args[1]?.method ?? (request instanceof Request ? request.method : 'GET');

    try {
      const response = await originalFetch(...args);
      captured.push({
        method,
        url,
        status: response.status,
        statusText: response.statusText,
        durationMs: Math.round(performance.now() - startedAt),
      });
      return response;
    } catch (error) {
      captured.push({
        method,
        url,
        error: error instanceof Error ? error.message : String(error),
        durationMs: Math.round(performance.now() - startedAt),
      });
      throw error;
    }
  };

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null,
  ) {
    const resolvedUrl = typeof url === 'string' ? url : url.toString();
    if (isForgeRelatedUrl(resolvedUrl)) {
      xhrMetadata.set(this, {
        method,
        url: resolvedUrl,
        startedAt: performance.now(),
      });
    }
    return nativeXhrOpen.call(this, method, url, async, username, password);
  };

  XMLHttpRequest.prototype.send = function (
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    const metadata = xhrMetadata.get(this);
    if (metadata) {
      this.addEventListener('loadend', () => {
        captured.push({
          method: metadata.method,
          url: metadata.url,
          status: this.status,
          statusText: this.statusText,
          durationMs: Math.round(performance.now() - metadata.startedAt),
        });
      });
    }
    return nativeXhrSend.call(this, body);
  };

  return {
    getRequests: () => [...captured],
    stop: () => {
      window.fetch = originalFetch;
      Object.defineProperty(xhrProto, 'open', xhrOpenDescriptor);
      Object.defineProperty(xhrProto, 'send', xhrSendDescriptor);
    },
  };
}

export async function runForgeLoadDiagnostics(
  documentId: string,
  viewerConfig: ForgeViewerConfig,
  capturedViewerRequests: ForgeCapturedNetworkRequest[] = [],
): Promise<ForgeLoadDiagnosticsReport> {
  const urn = stripUrnPrefix(documentId);
  const { forgeToken } = await getForgeViewerToken();
  const accessToken = forgeToken.credentials.access_token;
  const jwt = decodeJwtPayload(accessToken);

  const manifestProbes = await Promise.all(
    buildManifestProbeUrls(documentId).map(({ label, url }) =>
      probeUrl(label, url, accessToken).catch((error) => ({
        label,
        url,
        status: 0,
        statusText: 'Probe failed',
        body: error instanceof Error ? error.message : String(error),
      })),
    ),
  );

  let serverAccountDiagnostics: ForgeLoadDiagnosticsReport['serverAccountDiagnostics'] =
    null;
  try {
    serverAccountDiagnostics = await getForgeAccountDiagnostics(urn);
  } catch (error) {
    serverAccountDiagnostics = {
      forgeAppConfigured: false,
      forgeBucketName: 'unknown',
      configuredDerivativeRegion: 'EMEA',
      viewerStreamingApi: 'streamingV2_EU',
      authentication: {
        readToken: {
          ok: false,
          error: error instanceof Error ? error.message : String(error),
        },
        writeToken: { ok: false },
      },
      bucket: { name: 'unknown', accessible: false },
      modelDerivativeFormats: { eu: { ok: false }, us: { ok: false } },
      manifest: null,
      regionAlignment: null,
      analysis: {
        summary: 'Server diagnostics request failed',
        likelyCauses: ['Could not reach /api/forge/diagnostics'],
      },
    };
  }

  const report: ForgeLoadDiagnosticsReport = {
    documentId,
    viewerConfig,
    decodedUrn: decodeUrnBase64(urn),
    token: {
      expiresIn: forgeToken.credentials.expires_in,
      expiresAt: jwt?.exp ? new Date(jwt.exp * 1000).toISOString() : undefined,
      clientId: jwt?.client_id,
      scopes: normalizeJwtScopes(jwt?.scope),
      decodeError: jwt ? undefined : 'Could not decode JWT payload',
    },
    manifestProbes,
    capturedViewerRequests,
    serverAccountDiagnostics,
    analysis: { summary: '', likelyCauses: [] },
  };

  report.analysis = analyzeDiagnostics(
    manifestProbes,
    serverAccountDiagnostics,
    capturedViewerRequests,
    viewerConfig,
  );

  return report;
}
